/**
 * Tag utility functions for sorting, filtering, and manipulating tag arrays
 * @remarks
 * These utilities provide pure functions for working with tag collections.
 * They do not maintain state and can be used with any tag array source.
 */

import type { Tag } from "../types/types";

import { searchIncludes } from "@/utils/normalizeForSearch";

/**
 * Default color for tags when no board-specific color is set (blue)
 * @remarks
 * This is the single source of truth for the default tag color.
 * Import this constant instead of hardcoding color values to ensure consistency
 * across player cards, tag modals, and other tag-related UI components.
 */
export const DEFAULT_TAG_COLOR = "#3B82F6";

/**
 * Ensure a tag has a color property (adds default if missing)
 * @param tag - Tag object that may or may not have a color
 * @returns Tag object with color guaranteed to be present
 * @remarks
 * This is a temporary workaround for components that expect tags to have colors.
 * The GraphQL listKanbanTags query does not return colors (colors are board-specific).
 * For proper board-specific colors, use listKanbanBoardTags query instead.
 */
export function ensureTagColor(tag: Tag): Tag & { color: string } {
  return {
    ...tag,
    color: tag.color || DEFAULT_TAG_COLOR,
  };
}

/**
 * Ensure all tags in array have color properties (adds default if missing)
 * @param tags - Array of tags that may or may not have colors
 * @returns Array of tags with colors guaranteed to be present
 * @remarks
 * This is a temporary workaround for components that expect tags to have colors.
 * The GraphQL listKanbanTags query does not return colors (colors are board-specific).
 * For proper board-specific colors, use listKanbanBoardTags query instead.
 */
export function ensureTagsHaveColors(tags: Tag[]): (Tag & { color: string })[] {
  return tags.map(ensureTagColor);
}

/**
 * Get the most popular tags sorted by card count
 * @param tags - Array of tags to sort
 * @param limit - Maximum number of tags to return (default: 10)
 * @returns Array of tags sorted by card count in descending order
 */
export function getPopularTags(tags: Tag[], limit = 10): Tag[] {
  return [...tags].sort((a, b) => b.cardCount - a.cardCount).slice(0, limit);
}

/**
 * Search tags by name query using accent-insensitive comparison
 * @param tags - Array of tags to search
 * @param query - Search query string (accent-insensitive, case-insensitive, trimmed)
 * @returns Array of tags matching the query
 */
export function searchTags(tags: Tag[], query: string): Tag[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return tags;

  return tags.filter(tag => searchIncludes(tag.name, trimmedQuery));
}
